// Main TypeScript entry point
// Following performance requirements from prompt.md
import './utils/request-idle-callback-shim';
import SearchComponent from './components/SearchComponent';
import { i18n } from './utils/i18n';
import { fillHotelPrice } from './components/hotel-price-fill';

// Remove no-js class for progressive enhancement
document.documentElement.classList.remove('no-js');

// Lazy loading for images (if not natively supported)
if ('loading' in HTMLImageElement.prototype === false) {
  // Polyfill for older browsers
  import('./utils/lazy-loading').then(({ initLazyLoading }) => {
    initLazyLoading();
  });
}

// Initialize critical functionality
document.addEventListener('DOMContentLoaded', () => {
  // Skip link functionality
  initSkipLinks();

  // Performance monitoring
  if (process.env.NODE_ENV === 'production') {
    initPerformanceMonitoring();
  }
});

function initSkipLinks(): void {
  const skipLinks = document.querySelectorAll('.skip-link');

  skipLinks.forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      const target = document.querySelector(link.getAttribute('href') || '');
      if (target) {
        target.focus();
        target.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });
}

function initPerformanceMonitoring(): void {
  // Core Web Vitals monitoring
  if ('PerformanceObserver' in window) {
    // Monitor Largest Contentful Paint (LCP)
    new PerformanceObserver(list => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      console.log('LCP:', lastEntry.startTime);
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // Monitor First Input Delay (FID)
    new PerformanceObserver(list => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        console.log('FID:', entry.processingStart - entry.startTime);
      });
    }).observe({ entryTypes: ['first-input'] });

    // Monitor Cumulative Layout Shift (CLS)
    new PerformanceObserver(list => {
      let clsValue = 0;
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      console.log('CLS:', clsValue);
    }).observe({ entryTypes: ['layout-shift'] });
  }
}

function attachSearchComponents(): void {
  const searchComponents = document.querySelectorAll('[data-search-component]');
  if (searchComponents.length > 0) {
    const filtersEl = document.getElementById('search-filters-data');
    const filtersSrc = filtersEl?.getAttribute('data-src');

    if (filtersSrc) {
      fetch(filtersSrc)
        .then(res => {
          if (!res.ok)
            throw new Error(`Failed to load search filters: ${res.status}`);
          return res.json();
        })
        .then(filters => {
          searchComponents.forEach(
            element => new SearchComponent(element as HTMLElement, filters)
          );
        })
        .catch(err => console.error('Failed to load search filters:', err));
    }
  }
}

/**
 * Hands a sidebar that scrolled into view to the unit that prices it.
 *
 * All this supplies is the browser's collaborators: its `fetch`, the page's locale,
 * and the site's origin. What is requested and what is rendered lives in
 * `components/hotel-price-fill`, which has its own tests.
 */
const enhanceHotelPrice = (element: Element): Promise<void> =>
  fillHotelPrice(element as HTMLElement, {
    request: (url, init) => fetch(url, init),
    getLocale: () => i18n.getLocale(),
    siteUrl: process.env.SITE_URL as string,
  });

function initFeatureToggles(): void {
  document
    .querySelectorAll<HTMLButtonElement>('[data-unit-feature-toggle]')
    .forEach(button => {
      button.addEventListener('click', () => {
        const section = button.closest('[data-unit-feature-section]');
        const overflow = section?.querySelector('[data-unit-feature-overflow]');
        if (!overflow) return;

        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        const newExpanded = !isExpanded;

        button.setAttribute('aria-expanded', String(newExpanded));
        (overflow as HTMLElement).hidden = !newExpanded;

        const label = button.querySelector('[data-toggle-label]');
        if (label) {
          label.textContent = newExpanded
            ? (button.dataset.seeLessLabel ?? 'See less')
            : (button.dataset.seeMoreLabel ?? 'See more');
        }

        const icon = button.querySelector('.icon');
        if (icon) {
          icon.classList.toggle('rotate-180', newExpanded);
        }
      });
    });
}

function initHotelMap(): void {
  const container = document.getElementById('hotel-map');
  if (!container) return;

  const lat = parseFloat(container.dataset.lat ?? '');
  const lng = parseFloat(container.dataset.lng ?? '');
  const key = container.dataset.mapsKey ?? '';
  const hotelName = container.dataset.hotelName ?? 'Hotel';

  if (!lat || !lng || !key) return;

  let loaded = false;

  const loadMap = () => {
    if (loaded) return;
    loaded = true;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&callback=__initGoogleMap`;
    script.async = true;
    script.defer = true;

    (window as any).__initGoogleMap = () => {
      const position = { lat, lng };
      const map = new google.maps.Map(container, {
        center: position,
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: false,
        rotateControl: false,
        zoomControl: true,
        gestureHandling: 'cooperative',
      });

      new google.maps.Marker({
        position,
        map,
        title: hotelName,
      });
    };

    document.head.appendChild(script);
  };

  const observer = new IntersectionObserver(
    entries => {
      if (entries[0].isIntersecting) {
        loadMap();
        observer.disconnect();
      }
    },
    { rootMargin: '200px' }
  );

  observer.observe(container);
}

// using set timeout as a trick to separate into multiple tasks within browser
// (helps mitigate main thread blocking and long tasks)
window.requestIdleCallback(async () => {
  setTimeout(() => attachSearchComponents(), 0);
  createObserver('hotelPrice', '[data-hotel-price]', enhanceHotelPrice);
  setTimeout(() => initFeatureToggles(), 0);
  setTimeout(() => initHotelMap(), 0);
  setTimeout(() => loadTealium(), 0);
});

const observers: { [id: string]: IntersectionObserver } = {};

function createObserver(
  observerName: string,
  querySelector: string,
  func: Function
) {
  observers[observerName] = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          func(entry.target);
          observers[observerName].unobserve(entry.target);
        }
      });
    },
    { rootMargin: '100px' }
  );

  document.querySelectorAll(querySelector).forEach(item => {
    observers[observerName].observe(item);
  });
}

function loadTealium() {
  const envMap: Record<string, string> = {
    test: 'dev',
    dev: 'dev',
    staging: 'qa',
    sit: 'qa',
    prod: 'prod',
  };
  const tealiumEnv = envMap[process.env.ENVIRONMENT || 'dev'] || 'dev';

  const utagDefinition = document.createElement('script');
  utagDefinition.type = 'text/javascript';
  utagDefinition.innerHTML = 'var utag_data = { };';

  const tealiumScript = document.createElement('script');
  tealiumScript.type = 'text/javascript';
  tealiumScript.innerHTML = `(function(a,b,c,d){
      a='https://tags.tiqcdn.com/utag/tui/tui-sundeals/${tealiumEnv}/utag.js';
      b=document;c='script';d=b.createElement(c);d.src=a;d.type='text/java'+c;d.async=true;
      a=b.getElementsByTagName(c)[0];a.parentNode.insertBefore(d,a);
    })();`;

  document.head.appendChild(utagDefinition);
  document.head.appendChild(tealiumScript);
}
